// Tiny transparent PNG as base64.
const tinyTransparentPng = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

// Function to estimate memory
function estimateElementMemory(el) {
    if (el.tagName === 'IMG') {
        const width = el.naturalWidth || el.width || 0;
        const height = el.naturalHeight || el.height || 0;

        // RGBA pixels.
        return width * height * 4;
    } if (el.tagName === 'VIDEO') {
        // Estimate 30 frame buffer.
        const width = el.videoWidth || el.width || 0;
        const height = el.videoHeight || el.height || 0;

        return width * height * 4 * 30;
    } if (el.tagName === 'CANVAS') {
        const width = el.width || 0;
        const height = el.height || 0;

        // RGBA pixels
        return width * height * 4;
    }

    return 0;
}

// eslint-disable-next-line import/prefer-default-export
export function unload() {
    const stats = {
        unloadedImages: 0,
        unloadedVideos: 0,
        removedCanvases: 0,
        cleanedWebGL: 0,
        cleanedEventListeners: 0,
        estimatedSavings: 0,
    };

    // 1. Process images.
    function processImages() {
        // Regular DOM.
        // eslint-disable-next-line no-use-before-define
        document.querySelectorAll('img').forEach(processImage);

        // Shadow DOM.
        document.querySelectorAll('*').forEach((element) => {
            if (element.shadowRoot) {
                // eslint-disable-next-line no-use-before-define
                processShadowDOM(element.shadowRoot, 'img', processImage);
            }
        });

        // eslint-disable-next-line no-console
        console.log(`Processed ${stats.unloadedImages} images`);
    }

    function processImage(img) {
        if (img.hasAttribute('data-cz-unloaded-src') || img.src === tinyTransparentPng) {
            return;
        }

        const originalSrc = img.src;

        const isDataUrl = originalSrc.startsWith('data:');

        const originalWidth = img.width;
        const originalHeight = img.height;

        // Estimate memory.
        const memoryEstimate = estimateElementMemory(img);

        stats.estimatedSavings += memoryEstimate;

        // Preserve dimensions.
        if (originalWidth && originalHeight) {
            img.style.width = `${originalWidth}px`;
            img.style.height = `${originalHeight}px`;
        }

        // Store non-data URLs.
        if (!isDataUrl) {
            img.setAttribute('data-cz-unloaded-src', originalSrc);

            if (img.srcset) {
                img.setAttribute('data-cz-unloaded-srcset', img.srcset);
            }

            if (img.sizes) {
                img.setAttribute('data-cz-unloaded-sizes', img.sizes);
            }

            if (img.loading) {
                img.setAttribute('data-cz-unloaded-loading', img.loading);
            }
        } else {
            img.setAttribute('data-cz-was-data-url', 'true');
        }

        // Replace with tiny image.
        img.src = tinyTransparentPng;
        img.srcset = '';
        img.sizes = '';
        img.loading = 'lazy';
        img.classList.add('cz-unloaded-resource');

        stats.unloadedImages += 1;
    }

    // 2. Process videos - with enhanced state saving for restart.
    function processVideos() {
        // Regular DOM.
        // eslint-disable-next-line no-use-before-define
        document.querySelectorAll('video').forEach(processVideo);

        // Shadow DOM.
        document.querySelectorAll('*').forEach((element) => {
            if (element.shadowRoot) {
                // eslint-disable-next-line no-use-before-define
                processShadowDOM(element.shadowRoot, 'video', processVideo);
            }
        });

        // eslint-disable-next-line no-console
        console.log(`Processed ${stats.unloadedVideos} videos`);
    }

    function processVideo(video) {
        if (video.hasAttribute('data-cz-unloaded-video')) {
            return;
        }

        // Save complete video state
        const videoState = {
            src: video.src,
            currentTime: video.currentTime || 0,
            playbackRate: video.playbackRate,
            volume: video.volume,
            muted: video.muted,
            loop: video.loop,
            autoplay: video.autoplay,
            controls: video.controls,
            poster: video.poster,
            crossOrigin: video.crossOrigin,
            preload: video.preload,
            playbackQuality: video.getVideoPlaybackQuality ? JSON.stringify(video.getVideoPlaybackQuality()) : null,
            wasPlaying: !video.paused,
            tracks: Array.from(video.textTracks || []).map((track) => ({
                kind: track.kind,
                label: track.label,
                language: track.language,
                mode: track.mode,
            })),
        };

        // Store sources.
        const sources = Array.from(video.querySelectorAll('source')).map((source) => {
            const isSourceDataUrl = source.src && source.src.startsWith('data:');

            if (!isSourceDataUrl) {
                return {
                    src: source.src,
                    type: source.type,
                    srcset: source.srcset,
                };
            }

            return null;
        }).filter(Boolean);

        if (sources.length) {
            videoState.sources = sources;
        }

        // eslint-disable-next-line
        if (video._eventListeners) {
            // eslint-disable-next-line no-underscore-dangle
            videoState.eventListeners = video._eventListeners;
        }

        // Store complete state.
        video.setAttribute('data-cz-video-state', JSON.stringify(videoState));
        video.setAttribute('data-cz-unloaded-video', 'true');

        // Mark if it was a data URL.
        const isDataUrl = videoState.src && videoState.src.startsWith('data:');
        if (isDataUrl) {
            video.setAttribute('data-cz-was-data-url', 'true');
        }

        // Estimate memory.
        const memoryEstimate = estimateElementMemory(video);
        stats.estimatedSavings += memoryEstimate;

        // Pause and remove event listeners to prevent memory leaks.
        video.pause();

        // Remove sources and clean up.
        video.querySelectorAll('source').forEach((source) => source.remove());

        video.removeAttribute('src');

        // Force unloading of video data.
        video.load();

        video.poster = tinyTransparentPng;
        video.controls = false;
        video.autoplay = false;
        video.loop = false;
        video.classList.add('cz-unloaded-resource');

        stats.unloadedVideos += 1;
    }

    // Helper function to process elements in shadow DOM.
    function processShadowDOM(root, selector, processFn) {
        if (!root) {
            return;
        }

        // Process elements in this shadow root.
        root.querySelectorAll(selector).forEach(processFn);

        // Recursively process nested shadow roots.
        root.querySelectorAll('*').forEach((element) => {
            if (element.shadowRoot) {
                processShadowDOM(element.shadowRoot, selector, processFn);
            }
        });
    }

    // Function to restore images.
    function restoreImage(img) {
        // Skip data URLs as we don't store them
        if (img.hasAttribute('data-cz-was-data-url')) {
             // Can't restore data URLs.
            return 0;
        }

        if (img.hasAttribute('data-cz-unloaded-src')) {
            img.src = img.getAttribute('data-cz-unloaded-src');

            if (img.hasAttribute('data-cz-unloaded-srcset')) {
                img.srcset = img.getAttribute('data-cz-unloaded-srcset');
            }

            if (img.hasAttribute('data-cz-unloaded-sizes')) {
                img.sizes = img.getAttribute('data-cz-unloaded-sizes');
            }

            if (img.hasAttribute('data-cz-unloaded-loading')) {
                img.loading = img.getAttribute('data-cz-unloaded-loading');
            }

            img.classList.remove('cz-unloaded-resource');

            return 1;
        }

        return 0;
    }

    // Function to restore videos with proper restart
    function restoreVideo(video) {
        // Skip data URLs as we don't store them
        if (video.hasAttribute('data-cz-was-data-url')) {
            // Can't restore data URLs
            return 0;
        }

        if (video.hasAttribute('data-cz-video-state')) {
            try {
                const videoState = JSON.parse(video.getAttribute('data-cz-video-state'));

                // First rebuild sources if needed.
                if (videoState.sources && videoState.sources.length) {
                    videoState.sources.forEach((sourceData) => {
                        const source = document.createElement('source');

                        source.src = sourceData.src;
                        if (sourceData.type) {
                            source.type = sourceData.type;
                        }

                        if (sourceData.srcset) {
                            source.srcset = sourceData.srcset;
                        }

                        video.appendChild(source);
                    });
                } else if (videoState.src) {
                    video.src = videoState.src;
                }

                // Load the video to apply sources.
                video.load();

                // Apply all saved properties.
                if (videoState.poster) {
                    video.poster = videoState.poster;
                }

                if (videoState.crossOrigin) {
                    video.crossOrigin = videoState.crossOrigin;
                }

                if (videoState.preload) {
                    video.preload = videoState.preload;
                }

                video.controls = videoState.controls;
                video.loop = videoState.loop;
                video.muted = videoState.muted;
                video.volume = videoState.volume;
                video.autoplay = videoState.autoplay;
                video.playbackRate = videoState.playbackRate;

                // Set current time.
                if (videoState.currentTime > 0) {
                    video.currentTime = videoState.currentTime;
                }

                // Restore tracks if any.
                if (videoState.tracks && videoState.tracks.length) {
                    for (let i = 0; i < Math.min(video.textTracks.length, videoState.tracks.length); i += 1) {
                        video.textTracks[i].mode = videoState.tracks[i].mode;
                    }
                }

                // Set up one-time event listener to restart playback if needed
                if (videoState.wasPlaying) {
                    video.play();
                }

                video.classList.remove('cz-unloaded-resource');
                video.removeAttribute('data-cz-unloaded-video');

                return 1;
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('Error restoring video:', e);
                return 0;
            }
        }

        return 0;
    }

    // Function to restore elements in shadow DOM.
    function restoreShadowDOM(root) {
        if (!root) {
            return 0;
        }

        let restoredCount = 0;

        // Restore images in this shadow root.
        root.querySelectorAll('img.cz-unloaded-resource').forEach((img) => {
            restoredCount += restoreImage(img);
        });

        // Restore videos in this shadow root.
        root.querySelectorAll('video.cz-unloaded-resource').forEach((video) => {
            restoredCount += restoreVideo(video);
        });

        // Recursively restore in nested shadow roots
        root.querySelectorAll('*').forEach((element) => {
            if (element.shadowRoot) {
                restoredCount += restoreShadowDOM(element.shadowRoot);
            }
        });

        return restoredCount;
    }

    function restoreResources(options = {}) {
        const defaults = {
            images: true,
            videos: true,
            onlyVisible: false,
        };

        const settings = { ...defaults, ...options };

        let restoredCount = 0;

        function isElementVisible(el) {
            if (!settings.onlyVisible) {
                return true;
            }

            const rect = el.getBoundingClientRect();

            return (rect.top >= 0
                && rect.left >= 0
                && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
                && rect.right <= (window.innerWidth || document.documentElement.clientWidth)
            );
        }

        // Restore in regular DOM.
        if (settings.images) {
            document.querySelectorAll('img.cz-unloaded-resource').forEach((img) => {
                if (isElementVisible(img)) {
                    restoredCount += restoreImage(img);
                }
            });
        }

        if (settings.videos) {
            document.querySelectorAll('video.cz-unloaded-resource').forEach((video) => {
                if (isElementVisible(video)) {
                    restoredCount += restoreVideo(video);
                }
            });
        }

        // Restore in shadow DOM.
        document.querySelectorAll('*').forEach((element) => {
            if (element.shadowRoot) {
                restoredCount += restoreShadowDOM(element.shadowRoot);
            }
        });

        // eslint-disable-next-line no-console
        console.log(`Restored ${restoredCount} resources`);

        return restoredCount;
    }

    // Run all cleanup processes
    processImages();
    processVideos();

    // Log final statistics.
    const totalMB = (stats.estimatedSavings / (1024 * 1024)).toFixed(2);
    // eslint-disable-next-line no-console
    console.log('=== Memory Cleanup Summary ===');
    // eslint-disable-next-line no-console
    console.log(`Unloaded resources: ${stats.unloadedImages} images, ${stats.unloadedVideos} videos`);
    // eslint-disable-next-line no-console
    console.log(`Estimated memory savings: ~${totalMB} MB`);

    return restoreResources;
}
